import { ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Field, FieldDescription, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/Domain/components/Shared/Item/Item';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';

const SkeletonActionCard = ({ showMeta = true }: { showMeta?: boolean }): JSX.Element => (
  <div className="flex h-full flex-col gap-5 rounded-2xl border border-border bg-card p-6">
    <div className="flex items-center justify-between gap-1">
      <Skeleton className="size-10 rounded-lg" />
      <Skeleton className="h-6 w-10 rounded-full" />
    </div>
    <div className="space-y-2">
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-3.5 w-full" />
    </div>
    {showMeta && (
      <>
        <Skeleton className="h-6 w-28 rounded-full" />
        <Skeleton className="h-3.5 w-24" />
      </>
    )}
    <Separator className="mt-auto" />
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-28 rounded-md" />
      <Skeleton className="size-8 rounded-md" />
    </div>
  </div>
);

export const ActionsPageSkeleton = (): JSX.Element => {
  const { t } = useTranslation('actions_page');

  return (
    <div
      className="w-full max-w-7xl mx-auto flex flex-col h-full"
      data-testid="actions-page-skeleton"
    >
      <h1 className="text-lg font-semibold text-foreground">{t('title')}</h1>
      <Item
        variant="muted"
        className="mt-2 w-fit hover:bg-accent"
        size="sm"
        render={
          <a
            href="https://docs.quickchat.ai/ai-agent/actions"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ItemMedia variant="image">
              <img
                src="/static/assets/action_banner.png"
                alt={t('banner.title')}
                width={24}
                height={24}
                className="object-cover"
              />
            </ItemMedia>
            <ItemContent>
              <ItemTitle className="line-clamp-1">{t('banner.title')}</ItemTitle>
              <ItemDescription>{t('banner.description')}</ItemDescription>
            </ItemContent>
            <ItemActions>
              <ExternalLinkIcon className="ml-5 size-4 text-foreground-subtle" />
            </ItemActions>
          </a>
        }
      />

      <div className="space-y-8 mt-8 flex flex-col flex-1">
        <section>
          <Field>
            <FieldLabel>{t('quickchat_actions_title')}</FieldLabel>
            <FieldDescription>{t('quickchat_actions_subtitle')}</FieldDescription>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <SkeletonActionCard key={i} showMeta={false} />
              ))}
            </div>
          </Field>
        </section>

        <Separator className="mt-7" />
        <section className="flex flex-col flex-1 mb-6">
          <Field>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1.5">
                <FieldLabel>{t('api_actions_title')}</FieldLabel>
                <FieldDescription>{t('api_actions_description')}</FieldDescription>
              </div>
              <Skeleton className="h-9 w-40 rounded-md" />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 items-stretch">
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonActionCard key={i} />
              ))}
            </div>
          </Field>
        </section>
      </div>
    </div>
  );
};
